import { useEffect, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";

import { logger } from '../utils/logger';

// 🔧 use_categories.js
// ✅ Purpose: Shared category loader for LGV event pages
// ✅ Features:
// - Loads categories from /events/{eventId}/categories
// - Normalizes category names so object/string names are safe
// - Sorts by sortOrder first, then legacy numeric id, then name
// - Builds lookup map for both Firestore doc ID and legacy numeric ID
// - Preserves Bug Run / legacy cars.catid compatibility
// - Fully block-commented for clarity and future devs

export const useCategories = (eventId) => {
  const [categories, setCategories] = useState([]);
  const [categoriesMap, setCategoriesMap] = useState(undefined);
  const [loading, setLoading] = useState(true);

  const getCategoryNameText = (value, fallback = "Unnamed Category") => {
    if (value === null || value === undefined) {
      return fallback;
    }

    if (typeof value === "object") {
      return (
        value.name ||
        value.title ||
        value.description ||
        fallback
      );
    }

    return String(value);
  };

  const getCategorySortValue = (category) => {
    const sortOrder = Number(category?.sortOrder);

    if (Number.isFinite(sortOrder)) {
      return sortOrder;
    }

    const legacyId = Number(category?.id);

    if (Number.isFinite(legacyId)) {
      return legacyId;
    }

    const firestoreIdNumber = Number(category?.firestoreId);

    if (Number.isFinite(firestoreIdNumber)) {
      return firestoreIdNumber;
    }

    return 9999;
  };

  useEffect(() => {
    if (!eventId) return;

    const load = async () => {
      setLoading(true);

      try {
        const snap = await getDocs(
          collection(db, "events", eventId, "categories")
        );

        const docs = snap.docs
          .map((docSnap) => {
            const data = docSnap.data();

            const normalizedName =
              getCategoryNameText(data.name, "");

            return {
              ...data,

              // ✅ Firestore document ID is the modern stable ID.
              firestoreId: docSnap.id,

              // ✅ Keep `id` compatible with existing code.
              // If the category has a legacy numeric id, preserve it.
              // Otherwise fall back to Firestore document ID.
              id: data.id ?? docSnap.id,

              name: data.isDefault ? "" : normalizedName || "Unnamed Category",
            };
          })
          .filter((category) => {
            const isPlaceholder =
              category.firestoreId === "0" ||
              category.firestoreId === "_placeholder" ||
              String(category.name || "").toLowerCase() === "init";

            return !isPlaceholder;
          })
          .sort((a, b) => {
            const sortCompare =
              getCategorySortValue(a) - getCategorySortValue(b);

            if (sortCompare !== 0) {
              return sortCompare;
            }

            return String(a.name || "").localeCompare(
              String(b.name || ""),
              undefined,
              { sensitivity: "base" }
            );
          });

        setCategories(docs);

        const map = {};

        docs.forEach((category) => {
          // ✅ Modern lookup by Firestore doc ID.
          map[String(category.firestoreId)] = category.name;

          // ✅ Legacy lookup by numeric category id.
          map[String(category.id)] = category.name;

          // ✅ Optional explicit sortOrder lookup for older vote/entry paths.
          if (category.sortOrder !== undefined && category.sortOrder !== null) {
            map[String(category.sortOrder)] = category.name;
          }
        });

        setCategoriesMap(map);

        logger.log("✅ categoriesMap loaded:", {
          value: map,
        });
      } catch (err) {
        logger.error("❌ Failed to load categories:", {
          value: err,
        });
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [eventId]);

  return { categories, categoriesMap, loading };
};
